import React, { createContext, useContext, useMemo, useState } from 'react';

import {
	buildKpis,
	createGpsSession,
	generateRuleBasedSuggestion,
} from '@/lib/taxiAnalytics';
import { fetchClaudeSuggestion, syncToSpreadsheet } from '@/lib/taxiApi';
import { getCurrentCoordinates } from '@/lib/location';
import {
	clearActiveGpsSession,
	getActiveGpsSession,
	getGpsSessions,
	getTaxiLogs,
	getTaxiSettings,
	getUnsyncedData,
	markGpsSessionsSynced,
	markLogsSynced,
	removeTaxiLogById,
	saveActiveGpsSession,
	saveGpsSession,
	saveTaxiLog,
	saveTaxiSettings,
} from '@/lib/taxiStorage';
import { ActiveGpsSession, GpsSession, TaxiAppSettings, TaxiEvent, TaxiLog } from '@/types/taxi';

type CreateLogInput = {
	event: TaxiEvent;
	revenue: number | null;
	memo: string;
};

type TaxiDataContextValue = {
	logs: TaxiLog[];
	gpsSessions: GpsSession[];
	activeGpsSession: ActiveGpsSession | null;
	settings: TaxiAppSettings;
	suggestion: string;
	isSuggesting: boolean;
	kpis: ReturnType<typeof buildKpis>;
	createLog: (input: CreateLogInput) => Promise<TaxiLog>;
	removeLogById: (id: string) => void;
	startGpsSession: () => Promise<void>;
	endGpsSession: () => Promise<void>;
	updateSettings: (next: TaxiAppSettings) => void;
	refreshSuggestion: () => Promise<void>;
	syncUnsyncedData: () => Promise<string>;
};

const TaxiDataContext = createContext<TaxiDataContextValue | null>(null);

const createLogWithCurrentLocation = async (
	input: CreateLogInput,
): Promise<TaxiLog> => {
	const now = new Date();
	let lat: number | null = null;
	let lon: number | null = null;

	try {
		const coordinates = await getCurrentCoordinates();
		lat = coordinates.lat;
		lon = coordinates.lon;
	} catch (error) {
		console.warn('位置情報が取得できませんでした。ログは位置無しで保存します。', error);
	}

	return {
		id: crypto.randomUUID(),
		timestamp: now.toISOString(),
		event: input.event,
		revenue: input.revenue,
		memo: input.memo.trim(),
		lat,
		lon,
		weekday: now.getDay(),
		hour: now.getHours(),
		synced: false,
	};
};

type TaxiDataProviderProps = {
	children: React.ReactNode;
};

export const TaxiDataProvider = ({ children }: TaxiDataProviderProps) => {
	const [logs, setLogs] = useState<TaxiLog[]>(() => getTaxiLogs());
	const [gpsSessions, setGpsSessions] = useState<GpsSession[]>(() => getGpsSessions());
	const [activeGpsSession, setActiveGpsSession] = useState<ActiveGpsSession | null>(
		() => getActiveGpsSession(),
	);
	const [settings, setSettings] = useState<TaxiAppSettings>(() => getTaxiSettings());
	const [suggestion, setSuggestion] = useState<string>(() =>
		generateRuleBasedSuggestion(getTaxiLogs()),
	);
	const [isSuggesting, setIsSuggesting] = useState(false);

	const kpis = useMemo(() => buildKpis(logs, gpsSessions), [logs, gpsSessions]);

	const refreshSuggestion = async () => {
		setIsSuggesting(true);
		try {
			const ruleBasedSuggestion = generateRuleBasedSuggestion(logs);
			const aiSuggestion = await fetchClaudeSuggestion({
				apiKey: settings.claudeApiKey.trim(),
				logs,
				gpsSessions,
				ruleBasedSuggestion,
				defaultAreaHint: settings.defaultAreaHint.trim() || '新宿駅東口',
			});
			setSuggestion(aiSuggestion);
		} finally {
			setIsSuggesting(false);
		}
	};

	const createLog = async (input: CreateLogInput) => {
		const log = await createLogWithCurrentLocation(input);
		saveTaxiLog(log);
		const nextLogs = getTaxiLogs();
		setLogs(nextLogs);
		setSuggestion(generateRuleBasedSuggestion(nextLogs));
		return log;
	};

	const removeLogById = (id: string) => {
		removeTaxiLogById(id);
		const nextLogs = getTaxiLogs();
		setLogs(nextLogs);
		setSuggestion(generateRuleBasedSuggestion(nextLogs));
	};

	const startGpsSession = async () => {
		if (activeGpsSession) {
			throw new Error('既に待機セッションが開始されています');
		}
		const coordinates = await getCurrentCoordinates();
		const next: ActiveGpsSession = {
			id: crypto.randomUUID(),
			startTimestamp: new Date().toISOString(),
			startLat: coordinates.lat,
			startLon: coordinates.lon,
		};
		saveActiveGpsSession(next);
		setActiveGpsSession(next);
	};

	const endGpsSession = async () => {
		if (!activeGpsSession) {
			throw new Error('開始中の待機セッションがありません');
		}
		const coordinates = await getCurrentCoordinates();
		const session = createGpsSession(
			activeGpsSession.startTimestamp,
			activeGpsSession.startLat,
			activeGpsSession.startLon,
			new Date().toISOString(),
			coordinates.lat,
			coordinates.lon,
		);
		saveGpsSession(session);
		clearActiveGpsSession();
		setActiveGpsSession(null);
		setGpsSessions(getGpsSessions());
	};

	const updateSettings = (next: TaxiAppSettings) => {
		saveTaxiSettings(next);
		setSettings(next);
	};

	const syncUnsyncedData = async () => {
		const webhookUrl = settings.spreadsheetWebhookUrl.trim();
		if (!webhookUrl) {
			throw new Error('スプレッドシートWebhook URLを設定してください');
		}
		const unsynced = getUnsyncedData();
		if (unsynced.logs.length === 0 && unsynced.gpsSessions.length === 0) {
			return '未同期データはありません';
		}

		await syncToSpreadsheet(webhookUrl, {
			logs: unsynced.logs,
			gpsSessions: unsynced.gpsSessions,
			syncedAt: new Date().toISOString(),
		});
		markLogsSynced(unsynced.logs.map((log) => log.id));
		markGpsSessionsSynced(unsynced.gpsSessions.map((session) => session.id));
		setLogs(getTaxiLogs());
		setGpsSessions(getGpsSessions());

		return `${unsynced.logs.length}件のログ / ${unsynced.gpsSessions.length}件のGPSを同期しました`;
	};

	const value: TaxiDataContextValue = {
		logs,
		gpsSessions,
		activeGpsSession,
		settings,
		suggestion,
		isSuggesting,
		kpis,
		createLog,
		removeLogById,
		startGpsSession,
		endGpsSession,
		updateSettings,
		refreshSuggestion,
		syncUnsyncedData,
	};

	return (
		<TaxiDataContext.Provider value={value}>{children}</TaxiDataContext.Provider>
	);
};

export const useTaxiData = () => {
	const context = useContext(TaxiDataContext);
	if (!context) {
		throw new Error('useTaxiData must be used within TaxiDataProvider');
	}
	return context;
};
